import { Card, Field, Notice } from '@homecare/ui';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useApi } from '../../lib/api-hooks';

/**
 * Can this caregiver work on a given date? (§17.1, R-2b.)
 *
 * ## Why this is a separate panel from the credential list
 *
 * The list below it shows `cachedStatus` — what the nightly job last decided.
 * This panel calls `GET /v1/caregivers/:id/compliance`, which evaluates live
 * against the date in the box, from expiry dates rather than from that column.
 *
 * They can disagree, and when they do this one is right. A credential the job
 * last saw as VALID may have lapsed since; a renewal recorded this morning may
 * still read EXPIRED. §6.3.5 is explicit that the cached value is never trusted
 * alone for a blocking decision, and putting the two side by side — one
 * labelled by its date, the other not — is what stops somebody reading the
 * wrong one.
 *
 * ## The date box is the point
 *
 * §17.1: compliance is evaluated as of the **service date**, not today. A
 * coordinator planning three weeks out needs the answer for that day, and the
 * default of today is the answer to a different question. Making the date
 * editable here is what lets somebody see for themselves that the two differ.
 */

interface ComplianceIssue {
  credentialTypeId: string;
  credentialTypeName: string;
  reason: string;
  expiresAt: string | null;
  daysUntilExpiry: number | null;
  message: string;
}

interface ComplianceView {
  asOfDate: string;
  compliant: boolean;
  blocking: ComplianceIssue[];
  warnings: ComplianceIssue[];
}

export function CompliancePanel({ caregiverId }: { caregiverId: string }): ReactNode {
  const api = useApi();

  // Empty means "today", which the API resolves in the agency's timezone —
  // R-1e. Resolving it here would use the browser's zone, which is the
  // coordinator's and not the agency's.
  const [asOf, setAsOf] = useState('');

  const compliance = useQuery({
    queryKey: ['caregivers', 'compliance', caregiverId, asOf],
    queryFn: () =>
      api.get<ComplianceView>(
        `/v1/caregivers/${caregiverId}/compliance${asOf ? `?asOf=${asOf}` : ''}`,
      ),
    // Never cached: this is the answer something refuses on, and a stale one is
    // exactly the failure R-2b describes wearing a different hat.
    staleTime: 0,
  });

  const view = compliance.data;

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-ink text-lg font-bold">Can they work?</h2>
          <p className="text-ink-3 mt-1 text-sm">
            Worked out now, for the date you ask about — not from the statuses below.
          </p>
        </div>

        <Field
          label="Service date"
          type="date"
          hint="Leave blank for today."
          value={asOf}
          onChange={(event) => setAsOf(event.target.value)}
          data-testid="compliance-date"
        />
      </div>

      <ErrorNote error={compliance.error} nextStep="Reload the page and try again." />

      {compliance.isPending ? (
        <p className="text-ink-3 py-6 text-center text-sm">Checking…</p>
      ) : view === undefined ? null : (
        <div className="mt-4 grid gap-3" data-testid="compliance-answer">
          {view.compliant ? (
            <Notice tone="verified" title={`Yes — cleared to work on ${view.asOfDate}.`}>
              {view.warnings.length > 0 ? 'There are credentials worth chasing, below.' : undefined}
            </Notice>
          ) : (
            <Notice tone="critical" title={`No — cannot be scheduled on ${view.asOfDate}.`}>
              <ul className="grid gap-1" data-testid="compliance-blocking">
                {view.blocking.map((issue) => (
                  <li key={issue.credentialTypeId}>{issue.message}</li>
                ))}
              </ul>
            </Notice>
          )}

          {view.warnings.length > 0 && (
            <Notice tone="warning" title="Not blocking this date, but worth chasing">
              <ul className="grid gap-1" data-testid="compliance-warnings">
                {view.warnings.map((issue) => (
                  <li key={issue.credentialTypeId}>{issue.message}</li>
                ))}
              </ul>
            </Notice>
          )}
        </div>
      )}
    </Card>
  );
}
